/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useState, useEffect } from "react";

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/MultiSelectionSelect";

// NewUser page components
import FormField from "views/users/new-user/components/FormField";

import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import { getData } from "Utils/server";
import Permissions from "views/users/edit-user/ProfileComponentsCard/components/permissionsTransferList";

function UserInfo({ formData }) {
  const [checkChanges, setCheckChanges] = useState(false);
  const { formField, values, errors, touched } = formData;
  const {
    firstName,
    lastName,
    username,
    email,
    password,
    repeatPassword,
    platforms,
    roles,
    permissions,
  } = formField;
  const {
    firstName: firstNameV,
    lastName: lastNameV,
    username: usernameV,
    email: emailV,
    password: passwordV,
    repeatPassword: repeatPasswordV,
    platforms: platformsV,
    roles: rolesV,
    permissions: permissionsV,
  } = values;

  const [platformsOptions, setPlatformsOptions] = useState([]);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [permissionsOptions, setPermissionsOptions] = useState([]);
  const [userRoleSelected, setUserRoleSelected] = useState(0);

  const userData = getUserData();

  async function initData() {
    if (platformsOptions.length > 0) return;
    const platformsOptionsTmp = await getPlatformsData(userData.id);
    setPlatformsOptions(platformsOptionsTmp);

    const rolesOptionsTmp = await getRolesData();
    setRolesOptions(rolesOptionsTmp);
  }

  async function getPlatformsData(userId) {
    let platformsOptions = [];
    let getPlatformsEndpoint = `/api/v1/users/${userId}/platforms`;

    let response = await getData(getPlatformsEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      platformsOptions = response.data.map((platform) => {
        return { value: platform.id, label: platform.name };
      });
    }
    return platformsOptions;
  }

  async function getRolesData() {
    let rolesOptions = [];
    let getRolesEndpoint = `/api/v1/roles`;

    let response = await getData(getRolesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      rolesOptions = response.data.map((role) => {
        return { value: role.id, label: role.name };
      });
    }
    return rolesOptions;
  }

  const informParent = (changesExist, nextTab) => {
    values.permissions = [...changesExist];
  };

  useEffect(() => {
    if (formData) initData();
  }, [formData]);

  useEffect(() => {
    if (values.roles) setUserRoleSelected(values.roles);
  }, [values]);

  return (
    <SoftBox>
      <SoftBox>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Grid item xs={12} sm={12}>
              <FormField
                type={firstName.type}
                label={firstName.label}
                name={firstName.name}
                value={firstNameV}
                placeholder={firstName.placeholder}
                error={errors.firstName && touched.firstName}
                success={firstNameV.length > 0 && !errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                type={lastName.type}
                label={lastName.label}
                name={lastName.name}
                value={lastNameV}
                placeholder={lastName.placeholder}
                error={errors.lastName && touched.lastName}
                success={lastNameV.length > 0 && !errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                type={username.type}
                label={username.label}
                name={username.name}
                value={usernameV}
                placeholder={username.placeholder}
                error={errors.username && touched.username}
                success={usernameV.length > 0 && !errors.username}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                type={email.type}
                label={email.label}
                name={email.name}
                value={emailV}
                placeholder={email.placeholder}
                error={errors.email && touched.email}
                success={emailV.length > 0 && !errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                type={password.type}
                label={password.label}
                name={password.name}
                value={passwordV}
                placeholder={password.placeholder}
                error={errors.password && touched.password}
                success={passwordV.length > 0 && !errors.password}
                inputProps={{ autoComplete: "" }}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                type={repeatPassword.type}
                label={repeatPassword.label}
                name={repeatPassword.name}
                value={repeatPasswordV}
                placeholder={repeatPassword.placeholder}
                error={errors.repeatPassword && touched.repeatPassword}
                success={repeatPasswordV.length > 0 && !errors.repeatPassword}
                inputProps={{ autoComplete: "" }}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Grid item xs={12} sm={12}>
              <FormField
                label={platforms.name}
                name={platforms.label}
                options={platformsOptions}
                isMulti={true}
                placeholder={platforms.placeholder}
                component={SoftSelect}
                className="multiselectwrap"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormField
                label={roles.name}
                name={roles.label}
                options={rolesOptions}
                isMulti={false}
                placeholder={roles.placeholder}
                component={SoftSelect}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Permissions
                </SoftTypography>
              </SoftBox>
              <Permissions
                userId={null}
                className="transfer-list-holder"
                informParent={informParent}
                checkChanges={checkChanges}
                mode={"create"}
                role={userRoleSelected}
              ></Permissions>
            </Grid>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

// typechecking props for UserInfo
UserInfo.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
};

export default UserInfo;
